import React, { useState, useEffect } from 'react';
import { useIntersectionObserver } from './useIntersectionObserver';
import emailjs from '@emailjs/browser';
import SocialLinks from './SocialLinks';
import SEO from './SEO';

const AnimatedSection = ({ children }) => {
    const [ref, inView] = useIntersectionObserver({ threshold: 0.1 });
    return (
        <div ref={ref} className={`animated-section ${inView ? 'fade-in' : ''}`}>
            {children}
        </div>
    );
};

function Professionals() {
    const [proName, setProName] = useState('');
    const [proEmail, setProEmail] = useState('');
    const [proRole, setProRole] = useState('');
    const [proSubmitted, setProSubmitted] = useState(false);
    const [proLoading, setProLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const isProFormValid = isValidEmail(proEmail) && proName.trim().length >= 2 && proRole !== '';

    const handleProSubmit = async (e) => {
        e.preventDefault();
        if (isProFormValid) {
            setProLoading(true);
            try {
                const serviceId = 'service_v1k45tr';
                const templateId = 'template_emx7wyc';
                const publicKey = '8jrP4RXl7g5SLPcbO';

                const templateParams = {
                    user_email: proEmail,
                    user_name: proName,
                    reply_to: 'support@nouria.fit',
                    subject: `Professional Partnership Inquiry: ${proName} (${proRole})`,
                    message: `I would like to apply for the Professional Free Premium Membership.\n\nName: ${proName}\nEmail: ${proEmail}\nRole: ${proRole}`
                };

                await emailjs.send(serviceId, templateId, templateParams, publicKey);
                setProSubmitted(true);
            } catch (error) {
                console.error('Pro form submission error:', error);
                const subject = encodeURIComponent(`Professional Partnership Inquiry: ${proName} (${proRole})`);
                const body = encodeURIComponent(`I would like to apply for the Professional Free Premium Membership.\n\nName: ${proName}\nEmail: ${proEmail}\nRole: ${proRole}`);
                window.location.href = `mailto:support@nouria.fit?subject=${subject}&body=${body}`;
                setProSubmitted(true);
            } finally {
                setProLoading(false);
            }
        }
    };

    return (
        <main className="professionals-page">
            <SEO 
                title="Professional Partnerships & Health Insights"
                description="Join the Nouria professional network. Exclusive premium benefits for doctors, nutritionists, and fitness trainers."
                canonical="/professionals"
            />
            <section className="pro-hero" style={{ padding: '8rem 2rem 4rem', background: 'var(--primary-gradient)', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                <div className="pro-cta-bg-glow" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '150%', height: '150%', background: 'radial-gradient(circle, rgba(192, 138, 117, 0.25) 0%, transparent 70%)', zIndex: 1, pointerEvents: 'none' }}></div>
                <AnimatedSection>
                    <div className="container" style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap', textAlign: 'left' }}>
                        <img
                            src="/poses/pro-doctor.png"
                            alt="Health professional and doctor partnership"
                            loading="lazy"
                            style={{ width: '280px', height: 'auto', filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.35))' }}
                        />
                        <div style={{ flex: '1', minWidth: '300px', maxWidth: '650px' }}>
                            <div className="pro-cta-badge" style={{ background: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(10px)', padding: '0.5rem 1.5rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--secondary-color)', display: 'inline-block', marginBottom: '1.5rem', border: '1px solid rgba(192, 138, 117, 0.4)' }}>
                                Professional Excellence
                            </div>
                            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: '600', marginBottom: '1.5rem', lineHeight: '1.2' }}>
                                Partner with the <br />
                                <span style={{ color: 'var(--secondary-color)' }}>Future of Health</span>
                            </h1>
                            <p style={{ fontSize: '1.2rem', opacity: '0.9', fontWeight: '300', margin: 0 }}>
                                We're building the world's most advanced health OS, and we want you to be a part of it. If you're a medical professional, nutritionist, or trainer, contact us to see if you qualify for a <strong>Free Premium Annual Membership</strong>.
                            </p>
                        </div>
                    </div>
                </AnimatedSection>
            </section>

            <section className="pro-benefits" style={{ padding: '6rem 2rem', background: 'var(--background-color)' }}>
                <div className="container">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4rem', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ flex: '1', minWidth: '300px' }}>
                            <AnimatedSection>
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src="/poses/pro-running.png"
                                        alt="Active Professional"
                                        style={{ width: '100%', maxWidth: '400px', height: 'auto', filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.08))' }}
                                    />
                                </div>
                            </AnimatedSection>
                        </div>
                        <div style={{ flex: '1.2', minWidth: '300px' }}>
                            <AnimatedSection>
                                <h2 style={{ fontSize: '2.5rem', fontWeight: '600', marginBottom: '2.5rem', color: 'var(--primary-color)' }}>Why Join Our Professional Network?</h2>
                                <div style={{ display: 'grid', gap: '2rem' }}>
                                    {[
                                        { title: 'Free Premium Access', desc: 'Get a complimentary Annual Premium Membership to explore all our advanced tracking features.', icon: '🎟️' },
                                        { title: 'Early Feature Access', desc: 'Be the first to test our clinical-grade dashboards and patient monitoring tools.', icon: '🧪' },
                                        { title: 'Influencer Opportunities', desc: 'Contribute to our science-backed content and reach thousands of health-conscious users.', icon: '📣' },
                                        { title: 'Gut Health Insights', desc: 'Access proprietary data and research on fiber density and microbiome health.', icon: '🧬' }
                                    ].map((benefit, i) => (
                                        <div key={i} style={{ display: 'flex', gap: '1.5rem' }}>
                                            <span style={{ fontSize: '2rem', lineHeight: '1' }}>{benefit.icon}</span>
                                            <div>
                                                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--primary-color)' }}>{benefit.title}</h3>
                                                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', margin: 0 }}>{benefit.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </AnimatedSection>
                        </div>
                    </div>
                </div>
            </section>

            <section className="pro-form-section" style={{ padding: '6rem 2rem', background: 'var(--surface-color)' }}>
                <div className="container">
                    <div style={{ display: 'flex', flexWrap: 'wrap-reverse', gap: '4rem', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ flex: '1.2', minWidth: '300px' }}>
                            <AnimatedSection>
                                <div style={{ background: 'var(--background-color)', padding: '3rem', borderRadius: '24px', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)' }}>
                                    <h2 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--primary-color)' }}>Claim Your Professional Membership</h2>
                                    <p style={{ color: 'var(--text-secondary)', marginBottom: '2.5rem' }}>Fill out the form below and our team will review your credentials within 48 hours.</p>

                                    {!proSubmitted ? (
                                        <form onSubmit={handleProSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Your Full Name"
                                                    value={proName}
                                                    onChange={(e) => setProName(e.target.value)}
                                                    required
                                                    className="waitlist-input"
                                                    style={{ width: '100%', border: '1px solid var(--border-color)', background: 'var(--surface-color)', color: 'var(--text-main)', padding: '1rem 1.5rem', borderRadius: 'var(--radius-full)' }}
                                                />
                                                <input
                                                    type="email"
                                                    placeholder="Professional Email"
                                                    value={proEmail}
                                                    onChange={(e) => setProEmail(e.target.value)}
                                                    required
                                                    className="waitlist-input"
                                                    style={{ width: '100%', border: '1px solid var(--border-color)', background: 'var(--surface-color)', color: 'var(--text-main)', padding: '1rem 1.5rem', borderRadius: 'var(--radius-full)' }}
                                                />
                                            </div>
                                            <select
                                                value={proRole}
                                                onChange={(e) => setProRole(e.target.value)}
                                                required
                                                className="waitlist-input"
                                                style={{ width: '100%', border: '1px solid var(--border-color)', background: 'var(--surface-color)', color: 'var(--text-main)', padding: '1rem 1.5rem', borderRadius: 'var(--radius-full)', cursor: 'pointer', appearance: 'none' }}
                                            >
                                                <option value="" disabled>Select Your Profession</option>
                                                <option value="Medical Professional">Medical Professional</option>
                                                <option value="Nutritionist">Nutritionist</option>
                                                <option value="Dietician">Dietician</option>
                                                <option value="Fitness Trainer">Fitness Trainer</option>
                                                <option value="Researcher">Health Researcher</option>
                                                <option value="Other">Other Healthcare Professional</option>
                                            </select>
                                            <button
                                                type="submit"
                                                className="button button-primary"
                                                disabled={proLoading}
                                                style={{ width: '100%', padding: '1.25rem', borderRadius: '9999px', fontSize: '1.1rem', fontWeight: '600' }}
                                            >
                                                {proLoading ? 'Submitting...' : 'Claim Free Premium Membership'}
                                            </button>
                                        </form>
                                    ) : (
                                        <div className="fade-in" style={{ padding: '2.5rem', background: 'var(--primary-light)', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                                            <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>🎉</span>
                                            <h3 style={{ color: 'var(--primary-color)', marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: '600' }}>Application Received!</h3>
                                            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Thank you, {proName}. Our team will review your professional credentials and reach out to you at {proEmail} within 48 hours.</p>
                                        </div>
                                    )}
                                </div>
                            </AnimatedSection>
                        </div>
                        <div style={{ flex: '0.8', minWidth: '300px', textAlign: 'center' }}>
                            <AnimatedSection>
                                <img
                                    src="/poses/pro-lifting.png"
                                    alt="Training Professional"
                                    style={{ width: '100%', maxWidth: '320px', height: 'auto', filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.08))' }}
                                />
                                <div style={{ marginTop: '2.5rem' }}>
                                    <p style={{ fontWeight: '600', color: 'var(--primary-color)', marginBottom: '1.25rem' }}>Follow our clinical journey</p>
                                    <SocialLinks style={{ justifyContent: 'center' }} showLabel={true} />
                                </div>
                            </AnimatedSection>
                        </div>
                    </div>
                </div>
            </section>

            <section style={{ padding: '6rem 2rem', textAlign: 'center', background: 'var(--background-color)' }}>
                <div className="container">
                    <AnimatedSection>
                        <h2 style={{ fontSize: '2.25rem', fontWeight: '600', marginBottom: '2.5rem', color: 'var(--primary-color)' }}>Our Professional Partners</h2>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '3rem', opacity: '0.7' }}>
                            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>MEDICAL CLINICS</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>NUTRITION CENTERS</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>FITNESS ACADEMIES</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>DIETICIAN GUILDS</div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>
        </main>
    );
}

export default Professionals;
